<ng-template #contextNotes>
  <div
    class="flex flex-col h-full min-h-0"
    [class.gap-3]="isMobile"
    [class.gap-4]="!isMobile">
    <div
      class="flex-1 h-0 min-h-0 overflow-y-auto flex flex-col gap-2"
      [appScrollToBottom]="notes.length">
      @if (!isLegalOrg) {
        <app-details-sections
          [variant]="detailsFieldsVariant"
          [copilot]="copilot"
          [detailsBoxClasses]="detailsBoxClasses"
          [textClass]="detailsRowClasses"
          [emptyClass]="detailsPlaceholderClasses"
          personalDetailsTestId="encounter-content-personal-details"
          visitDetailsTestId="encounter-content-visit-details" />
      }
      <div class="flex-1 min-h-0" aria-hidden="true"></div>
      <app-collapsible-list
        class="shrink-0"
        [title]="'Added context' | translate"
        [items]="contextItems"
        [emptyText]="'No context' | translate"
        [expanded]="true"
        [removable]="true"
        [striped]="true"
        [classes]="addedContextClasses"
        (remove)="onRemoveFromList($event)"
        data-testid="encounter-content-added-context" />
    </div>

    <div
      class="shrink-0 flex flex-col rounded-[7px]"
      [ngClass]="isMobile
        ? 'border border-surface-dim bg-white px-5 py-4'
        : desktopComposerClass"
      data-testid="encounter-content-composer">
      <label class="sr-only" for="encounter-content-draft">{{ 'Type a note' | translate }}</label>
      <textarea
        #contextInput
        id="encounter-content-draft"
        class="w-full resize-none bg-transparent border-0 outline-none"
        [ngClass]="isMobile
          ? 'body-medium min-h-[56px] text-on-surface placeholder:text-on-surface-variant'
          : 'content-panel-body min-h-[72px]'"
        [(ngModel)]="noteDraft"
        [placeholder]="('Type a note' | translate) + '…'"
        (keydown)="onNoteKeydown($event)"
        data-testid="encounter-content-input"></textarea>
    </div>
  </div>
</ng-template>

@if (isMobile) {
  <app-bottom-sheet
    #bottomSheet
    [collapsedHeightPx]="collapsedHeightPx"
    [handleAriaLabel]="'Expand or collapse content panel' | translate"
    [backdropAriaLabel]="'Collapse content panel' | translate"
    (stateChange)="onSheetStateChange($event)">

    <div class="shrink-0">
      <mat-chip-listbox
        class="content-panel-chips px-3 pb-2"
        [hideSingleSelectionIndicator]="true"
        [value]="selectedMobileView"
        (change)="onChipsChange($event.value)">
        <mat-chip-option
          value="transcript"
          class="flex-1"
          [disabled]="!transcriptEnabled"
          (selectionChange)="keepSelected($event)">
          {{ 'Transcript' | translate }}
        </mat-chip-option>

        <mat-chip-option
          value="context"
          class="flex-1"
          (selectionChange)="keepSelected($event)">
          {{ 'Context' | translate }}
        </mat-chip-option>
      </mat-chip-listbox>
    </div>

    <div
      class="flex flex-1 min-h-0 flex-col overflow-hidden"
      data-testid="encounter-content-panel-mobile-body">

      <div
        class="grid min-h-0"
        [ngClass]="[
          selectedMobileView === 'context'
            ? 'flex-1 grid-rows-[1fr]'
            : 'grid-rows-[0fr]',
          isExpanded
            ? 'transition-[grid-template-rows] duration-600 ease-in-out motion-reduce:transition-none'
            : 'transition-none'
        ]">
        <div
          class="min-h-0"
          [ngClass]="[
            selectedMobileView === 'context'
              ? 'opacity-100 translate-x-0 overflow-hidden'
              : 'opacity-0 -translate-x-2 pointer-events-none overflow-hidden',
            isExpanded
              ? 'transition-[opacity,transform] duration-600 ease-in-out motion-reduce:transition-none'
              : 'transition-none'
          ]">
          <div class="flex flex-col h-full min-h-0 pt-2 pb-6 px-3 bg-surface-container-low">
            <ng-container *ngTemplateOutlet="contextNotes" />
          </div>
        </div>
      </div>

      <div
        class="grid min-h-0"
        [ngClass]="[
          selectedMobileView === 'transcript'
            ? 'flex-1 grid-rows-[1fr]'
            : 'grid-rows-[0fr]',
          isExpanded
            ? 'transition-[grid-template-rows] duration-600 ease-in-out motion-reduce:transition-none'
            : 'transition-none'
        ]">
        <div
          class="h-full min-h-0 overflow-hidden"
          [ngClass]="[
            selectedMobileView === 'transcript'
              ? 'opacity-100 translate-x-0'
              : 'opacity-0 translate-x-2 pointer-events-none',
            isExpanded
              ? 'transition-[opacity,transform] duration-600 ease-in-out motion-reduce:transition-none'
              : 'transition-none'
          ]">
          <app-transcript-list
            class="h-full"
            [utterances]="utterances"
            variant="light" />
        </div>
      </div>
    </div>
  </app-bottom-sheet>
} @else {
  @if (showClose) {
    <div class="encounter-content-panel-close">
      <button
        mat-icon-button
        type="button"
        (click)="closed.emit()"
        [attr.aria-label]="'Close' | translate"
        data-testid="encounter-content-panel-close">
        <mat-icon class="!text-lg">close</mat-icon>
      </button>
    </div>
  }

  <mat-tab-group
    class="encounter-content-panel-tabs"
    [mat-stretch-tabs]="false"
    [selectedIndex]="selectedTabIndex"
    (selectedTabChange)="onTabChange($event.index)">
    <mat-tab [disabled]="!transcriptEnabled">
      <ng-template mat-tab-label>
        <span class="flex items-center gap-2">
          <mat-icon svgIcon="page-flip" class="icon-size [--icon-size:16px]"></mat-icon>
          <span>{{ 'Transcript' | translate }}</span>
        </span>
      </ng-template>
      <div class="h-full min-h-0" [ngClass]="uiState === 'recording' ? 'bg-black/16' : null">
        <app-transcript-list class="h-full" [utterances]="utterances" variant="dark" />
      </div>
    </mat-tab>

    <mat-tab>
      <ng-template mat-tab-label>
        <span class="flex items-center gap-2">
          <mat-icon svgIcon="medium" class="icon-size [--icon-size:16px]"></mat-icon>
          <span>{{ 'Context' | translate }}</span>
        </span>
      </ng-template>
      <div class="flex flex-col h-full min-h-0 pt-2 pb-6 px-3.5" [ngClass]="uiState === 'recording' ? 'bg-black/16' : null">
        <ng-container *ngTemplateOutlet="contextNotes" />
      </div>
    </mat-tab>
  </mat-tab-group>
}
